<script setup lang="ts">
	import { computed, inject, onMounted, ref, type Ref } from "vue";
	import { fetchAuthSession } from "@aws-amplify/auth";
	import { useSettings } from "@/composables/settings";
	import { useQueryClient } from "@tanstack/vue-query";
	import { getApiUrl, awsConfiguration } from "@/aws-configuration";
	import { initFlightpathServiceClient } from "@ptx-autonomous/libs/flightpath-service-api";
	import { useApiGatewayStage } from "@shared/composables";
	import {
		setOrganizationId,
		useOrganizationsQuery,
	} from "@ptx-autonomous/libs/api";
	import type { AccountDto } from "@ptx-autonomous/libs/flightpath-service-api-models";
	import { useFlightpathServiceHttp } from "@ptx-autonomous/libs/flightpath-service-api";
	import version from "@/version";
	import type { CognitoIdTokenPayload } from "@shared/types/auth";

	const emit = defineEmits<{
		(e: "close"): void;
		(e: "reset"): void;
	}>();

	const user = ref<CognitoIdTokenPayload | null>(null);

	const isFlightPathAdmin = computed(() => {
		const groups = user.value?.["cognito:groups"] as string[] | undefined;
		return groups?.includes("FlightPathAdmin") ?? false;
	});

	const initializeApiClients = async () => {
		try {
			const session = await fetchAuthSession();
			if (session.tokens?.idToken) {
				user.value = session.tokens.idToken
					.payload as CognitoIdTokenPayload;
				return true;
			}
			return false;
		} catch (error) {
			console.error("Failed to initialize API clients:", error);
			return false;
		}
	};

	const { apiVersion, organizationId } = useSettings();
	type SettingsTab = "general" | "tractor";
	const activeTab = ref<SettingsTab>("general");

	const tractorTurningRadius = inject<
		Ref<{
			feet: number;
			inches: number;
		}>
	>("tractorTurningRadius")!;
	const tractorImplementTurningRadius = inject(
		"tractorImplementTurningRadius",
	) as Ref<{ feet: number; inches: number }>;
	const rearAxleToHitch = inject("rearAxleToHitch") as Ref<{
		feet: number;
		inches: number;
	}>;

	const queryClient = useQueryClient();

	// API Gateway stages for environment switching
	const { stages, errorMessage } = useApiGatewayStage(
		awsConfiguration.restApiId,
	);
	const sortedStages = computed(() => {
		const stageList = stages.value ?? [];
		const betaStage = stageList.find(stage => {
			const name = (stage.Name || "").toLowerCase();
			return name === "beta";
		});
		const prodStage = stageList.find(stage => {
			const name = (stage.Name || "").toLowerCase();
			return name === "prod";
		});
		const otherStages = stageList.filter(stage => {
			const name = (stage.Name || "").toLowerCase();
			return name !== "beta" && name !== "prod";
		});
		return [betaStage, prodStage].filter(Boolean).concat(otherStages);
	});

	// Organizations query for admin users
	const http = useFlightpathServiceHttp();
	const activeOrg = ref<boolean>(true);
	const { isLoading: isOrganizationLoading, data: organizations } =
		useOrganizationsQuery(http, undefined, undefined, activeOrg, undefined);

	const sortedOrganizations = computed(() => {
		if (!organizations.value) return [];
		return [...organizations.value].sort((a, b) => {
			const nameA = (a.name || a.id || "").toLowerCase();
			const nameB = (b.name || b.id || "").toLowerCase();
			return nameA.localeCompare(nameB);
		});
	});

	onMounted(() => {
		initializeApiClients();
	});

	function closeDialog() {
		emit("close");
	}

	async function switchOrganization(account: AccountDto) {
		if (organizationId.value !== account.id) {
			emit("reset"); // Clear all path planning state
			organizationId.value = account.id as string;
			await setOrganizationId(account.id as string);
			await initializeApiClients();
			queryClient.resetQueries();
		}
	}

	async function switchEnvironment(environment: string) {
		if (apiVersion.value !== environment) {
			emit("reset"); // Clear all path planning state
			apiVersion.value = environment;
			initFlightpathServiceClient(getApiUrl(environment));
			await initializeApiClients();
			queryClient.resetQueries();
		}
	}
</script>

<template>
	<div class="fixed inset-0 z-50 flex items-center justify-center">
		<div
			class="absolute inset-0 bg-overlay"
			@click="closeDialog"
		></div>

		<div
			class="relative bg-white shadow-xl rounded-2xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col"
		>
			<div
				class="flex items-center justify-between p-6 bg-green-50 border-b border-green-200 rounded-t-2xl flex-shrink-0"
			>
				<h2 class="title text-green-800">Settings</h2>
				<button
					@click="closeDialog"
					class="text-grey-400 hover:text-grey-600 p-1"
				>
					<svg
						class="w-6 h-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M6 18L18 6M6 6l12 12"
						></path>
					</svg>
				</button>
			</div>

			<div class="border-b border-grey-200 flex-shrink-0">
				<nav class="flex px-6 -mb-px">
					<button
						class="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
						:class="
							activeTab === 'general'
								? 'border-green-600 text-green-700 bg-green-50'
								: 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
						"
						@click="activeTab = 'general'"
					>
						General
					</button>
					<button
						class="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
						:class="
							activeTab === 'tractor'
								? 'border-green-600 text-green-700 bg-green-50'
								: 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
						"
						@click="activeTab = 'tractor'"
					>
						Tractor
					</button>
				</nav>
			</div>

			<div class="p-6 space-y-8 overflow-y-auto flex-1 min-h-0">
				<!-- General Tab -->
				<template v-if="activeTab === 'general'">
					<div>
						<h3 class="text-lg font-medium text-grey-900 mb-4">
							Environment
						</h3>
						<div
							v-if="sortedStages.length > 0"
							class="space-y-2"
						>
							<div
								v-for="stage in sortedStages"
								:key="stage.LambdaAlias"
								class="flex items-center p-3 rounded-lg border cursor-pointer transition-colors"
								:class="
									apiVersion === stage.LambdaAlias
										? 'border-green-600 bg-green-50 ring-2 ring-green-600 ring-offset-2'
										: 'border-grey-200 hover:border-green-600 hover:bg-green-50'
								"
								@click="switchEnvironment(stage.LambdaAlias)"
							>
								<div class="flex items-center">
									<div
										class="w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center"
										:class="
											apiVersion === stage.LambdaAlias
												? 'border-green-600 bg-green-600'
												: 'border-grey-300'
										"
									>
										<div
											v-if="
												apiVersion === stage.LambdaAlias
											"
											class="w-2 h-2 bg-white rounded-full"
										></div>
									</div>
									<div>
										<div class="font-medium text-grey-900">
											{{ stage.Name }}
										</div>
										<div class="text-sm text-grey-500">
											{{ stage.LambdaAlias }}
										</div>
									</div>
								</div>
							</div>
						</div>
						<div
							v-else-if="errorMessage"
							class="text-center py-4 text-orange-800"
						>
							Environment switching not available
						</div>
						<div
							v-else
							class="text-center py-8"
						>
							<div
								class="animate-spin rounded-full h-8 w-8 border-2 border-green-50 border-b-2 border-b-green-600 mx-auto mb-3"
							></div>
							<div class="text-sm font-medium text-grey-600">
								Loading environments...
							</div>
						</div>
					</div>

					<div v-if="isFlightPathAdmin">
						<h3 class="text-lg font-medium text-grey-900 mb-4">
							Organization
						</h3>
						<div
							v-if="isOrganizationLoading"
							class="text-center py-8"
						>
							<div
								class="animate-spin rounded-full h-8 w-8 border-2 border-green-50 border-b-2 border-b-green-600 mx-auto mb-3"
							></div>
							<div class="text-sm font-medium text-grey-600">
								Loading organizations...
							</div>
						</div>
						<div
							v-else-if="
								sortedOrganizations &&
								sortedOrganizations.length > 0
							"
							class="space-y-2"
						>
							<div
								v-for="org in sortedOrganizations"
								:key="org.id"
								class="flex items-center p-3 rounded-lg border cursor-pointer transition-colors"
								:class="
									organizationId === org.id
										? 'border-green-600 bg-green-50 ring-2 ring-green-600 ring-offset-2'
										: 'border-grey-200 hover:border-green-600 hover:bg-green-50'
								"
								@click="switchOrganization(org)"
							>
								<div class="flex items-center w-full">
									<div
										class="w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center"
										:class="
											organizationId === org.id
												? 'border-green-600 bg-green-600'
												: 'border-grey-300'
										"
									>
										<div
											v-if="organizationId === org.id"
											class="w-2 h-2 bg-white rounded-full"
										></div>
									</div>
									<div class="flex-1">
										<div class="font-medium text-grey-900">
											{{ org.name || org.id }}
										</div>
										<div class="text-sm text-grey-500">
											{{ org.id }}
										</div>
									</div>
								</div>
							</div>
						</div>
						<div
							v-else
							class="text-center py-4 text-grey-500"
						>
							No organizations available
						</div>
					</div>
				</template>

				<!-- Tractor Tab -->
				<template v-if="activeTab === 'tractor'">
					<div>
						<h3 class="text-lg font-medium text-grey-900 mb-4">
							Tractor Settings
						</h3>
						<div class="space-y-4">
							<div>
								<label
									class="block text-sm font-medium text-grey-700 mb-1"
								>
									Tractor Turning Radius
								</label>
								<div class="flex gap-2">
									<div class="flex-1 relative">
										<input
											id="tractorTurningRadiusFt"
											v-model.number="
												tractorTurningRadius.feet
											"
											type="number"
											step="1"
											min="0"
											class="w-full pl-3 pr-8 py-2 border border-grey-300 rounded-lg text-grey-900 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
										/>
										<span
											class="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-grey-400"
											>ft</span
										>
									</div>
									<div class="flex-1 relative">
										<input
											id="tractorTurningRadiusIn"
											v-model.number="
												tractorTurningRadius.inches
											"
											type="number"
											step="1"
											min="0"
											max="11"
											class="w-full pl-3 pr-8 py-2 border border-grey-300 rounded-lg text-grey-900 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
										/>
										<span
											class="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-grey-400"
											>in</span
										>
									</div>
								</div>
							</div>
							<div>
								<label
									class="block text-sm font-medium text-grey-700 mb-1"
								>
									Tractor-Implement Turning Radius
								</label>
								<div class="flex gap-2">
									<div class="flex-1 relative">
										<input
											id="tractorImplementTurningRadiusFt"
											v-model.number="
												tractorImplementTurningRadius.feet
											"
											type="number"
											step="1"
											min="0"
											class="w-full pl-3 pr-8 py-2 border border-grey-300 rounded-lg text-grey-900 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
										/>
										<span
											class="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-grey-400"
											>ft</span
										>
									</div>
									<div class="flex-1 relative">
										<input
											id="tractorImplementTurningRadiusIn"
											v-model.number="
												tractorImplementTurningRadius.inches
											"
											type="number"
											step="1"
											min="0"
											max="11"
											class="w-full pl-3 pr-8 py-2 border border-grey-300 rounded-lg text-grey-900 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
										/>
										<span
											class="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-grey-400"
											>in</span
										>
									</div>
								</div>
							</div>
							<div>
								<label
									class="block text-sm font-medium text-grey-700 mb-1"
								>
									Tractor Rear Axle to Hitch
								</label>
								<div class="flex gap-2">
									<div class="flex-1 relative">
										<input
											id="rearAxleToHitchFt"
											v-model.number="
												rearAxleToHitch.feet
											"
											type="number"
											step="1"
											min="0"
											class="w-full pl-3 pr-8 py-2 border border-grey-300 rounded-lg text-grey-900 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
										/>
										<span
											class="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-grey-400"
											>ft</span
										>
									</div>
									<div class="flex-1 relative">
										<input
											id="rearAxleToHitchIn"
											v-model.number="
												rearAxleToHitch.inches
											"
											type="number"
											step="1"
											min="0"
											max="11"
											class="w-full pl-3 pr-8 py-2 border border-grey-300 rounded-lg text-grey-900 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
										/>
										<span
											class="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-grey-400"
											>in</span
										>
									</div>
								</div>
							</div>
						</div>
					</div>
				</template>
			</div>

			<div
				class="p-6 pt-4 border-t border-grey-200 flex-shrink-0 rounded-b-2xl"
			>
				<div class="flex items-center justify-between">
					<div class="flex items-center gap-3">
						<img
							src="/ptx-logo.svg"
							alt="PTX Logo"
							class="h-8"
						/>
						<div class="text-sm">
							<div class="font-medium text-grey-900">
								{{ user?.email || "User" }}
							</div>
							<div class="text-grey-500">
								{{ isFlightPathAdmin ? "Admin" : "User" }}
							</div>
						</div>
					</div>
					<div class="text-grey-400">v{{ version }}</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
	.bg-overlay {
		background-color: rgba(0, 0, 0, 0.2);
	}
</style>
